// react-native-bluetooth-serial -> browser printing.
// The app sends ESC/POS bytes to the printer. They are collected here,
// converted to a printable receipt and handed to the browser print dialog.
const FLUSH_DELAY = 800;

let chunks = [];
let timer = null;

function toBytes(data) {
  if (data === null || data === undefined) {
    return new Uint8Array(0);
  }
  if (typeof data === "string") {
    return new TextEncoder().encode(data);
  }
  if (data instanceof Uint8Array) {
    return data;
  }
  if (data.buffer instanceof ArrayBuffer) {
    return new Uint8Array(data.buffer, data.byteOffset || 0, data.byteLength);
  }
  if (Array.isArray(data)) {
    return Uint8Array.from(data);
  }
  return new TextEncoder().encode(String(data));
}

const defaultStyle = () => ({
  size: 0,
  align: 0,
  bold: false,
  underline: false,
  italic: false,
});

// Turns an ESC/POS byte stream into lines of styled text
export function parseEscPos(bytes) {
  const lines = [];
  const decoder = new TextDecoder("utf-8");
  let style = defaultStyle();
  let segments = [];
  let text = [];

  const endSegment = () => {
    if (text.length) {
      segments.push({
        text: decoder.decode(Uint8Array.from(text)),
        style: Object.assign({}, style),
      });
      text = [];
    }
  };
  const endLine = () => {
    endSegment();
    lines.push({ align: style.align, segments });
    segments = [];
  };

  let i = 0;
  while (i < bytes.length) {
    const byte = bytes[i];
    if (byte === 0x1b) {
      const cmd = bytes[i + 1];
      const arg = bytes[i + 2];
      endSegment();
      if (cmd === 0x40) {
        style = defaultStyle();
        i += 2;
      } else if (cmd === 0x21) {
        style.size = arg & 0x30;
        i += 3;
      } else if (cmd === 0x61) {
        style.align = arg;
        i += 3;
      } else if (cmd === 0x45) {
        style.bold = !!arg;
        i += 3;
      } else if (cmd === 0x2d) {
        style.underline = !!arg;
        i += 3;
      } else if (cmd === 0x34) {
        style.italic = true;
        i += 2;
      } else if (cmd === 0x35) {
        style.italic = false;
        i += 2;
      } else if (cmd === 0x70) {
        i += 5; // cash drawer kick
      } else if (cmd === 0x64) {
        for (let n = 0; n < (arg || 1); n += 1) {
          endLine();
        }
        i += 3;
      } else {
        i += 2;
      }
    } else if (byte === 0x1d) {
      endSegment();
      i += 3; // paper cut and other GS commands
    } else if (byte === 0x0a) {
      endLine();
      i += 1;
    } else if (byte === 0x0d) {
      i += 1;
    } else if (byte === 0x09) {
      text.push(0x20, 0x20, 0x20, 0x20);
      i += 1;
    } else {
      text.push(byte);
      i += 1;
    }
  }
  if (text.length || segments.length) {
    endLine();
  }
  return lines;
}

const escapeHtml = value =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export function toHtml(lines) {
  const aligns = ["left", "center", "right"];
  const body = lines
    .map(line => {
      const content = line.segments
        .map(({ text, style }) => {
          const css = [];
          if (style.size === 0x30) {
            css.push("font-size:2em;line-height:1.1");
          } else if (style.size === 0x10) {
            css.push("font-size:1.5em;line-height:1.1");
          } else if (style.size === 0x20) {
            css.push("font-size:1.5em;letter-spacing:.1em");
          }
          if (style.bold) {
            css.push("font-weight:bold");
          }
          if (style.underline) {
            css.push("text-decoration:underline");
          }
          if (style.italic) {
            css.push("font-style:italic");
          }
          return `<span style="${css.join(";")}">${escapeHtml(text)}</span>`;
        })
        .join("");
      return `<div class="line" style="text-align:${aligns[line.align] ||
        "left"}">${content || "&nbsp;"}</div>`;
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title>
<style>
  @page { margin: 4mm; }
  body { margin: 0; font-family: "Courier New", Courier, monospace; font-size: 12px; color: #000; }
  .receipt { width: 32ch; margin: 0 auto; }
  .line { white-space: pre-wrap; word-break: break-word; min-height: 1.2em; line-height: 1.2; }
</style></head><body><div class="receipt">${body}</div></body></html>`;
}

function print(html) {
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } finally {
      setTimeout(() => {
        if (frame.parentNode) {
          frame.parentNode.removeChild(frame);
        }
      }, 60000);
    }
  }, 150);
}

function flush() {
  timer = null;
  const size = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const bytes = new Uint8Array(size);
  let offset = 0;
  chunks.forEach(chunk => {
    bytes.set(chunk, offset);
    offset += chunk.length;
  });
  chunks = [];

  const lines = parseEscPos(bytes);
  const hasText = lines.some(line =>
    line.segments.some(segment => segment.text.trim().length > 0),
  );
  if (hasText) {
    window.__lastReceiptHtml = toHtml(lines);
    print(window.__lastReceiptHtml);
  }
}

const BluetoothSerial = {
  isEnabled: () => Promise.resolve(true),
  enable: () => Promise.resolve(true),
  disable: () => Promise.resolve(true),
  requestEnable: () => Promise.resolve(true),
  isConnected: () => Promise.resolve(true),
  connect: () => Promise.resolve({ message: "Connected to Browser Printer" }),
  disconnect: () => Promise.resolve(true),
  list: () =>
    Promise.resolve([{ id: "BROWSER-PRINTER", name: "Browser Printer" }]),
  discoverUnpairedDevices: () => Promise.resolve([]),
  cancelDiscovery: () => Promise.resolve(true),
  write(data) {
    chunks.push(toBytes(data));
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(flush, FLUSH_DELAY);
    return Promise.resolve(true);
  },
  on() {},
  removeListener() {},
  withDelimiter: () => Promise.resolve(true),
};

export default BluetoothSerial;
